import type { EmailBrandDetectionReview, EmailBrandLookRead, VoiceState } from '../../services/api'

/**
 * The Brand tab's own sections (MAR-384) — the `?section=` vocabulary beside `?tab=voice`, read
 * the same way Limits reads its own (`limitsGlance.ts`): from the URL alone, at the read.
 */
export type BrandSection = 'overview' | 'sound' | 'look'

export const BRAND_SECTION_IDS: BrandSection[] = ['overview', 'sound', 'look']
const DEFAULT_SECTION: BrandSection = 'overview'

/** The open section, from the URL alone. An unknown or missing value opens the overview. */
export const brandSectionFrom = (params: URLSearchParams): BrandSection => {
  const raw = params.get('section')
  return raw && (BRAND_SECTION_IDS as string[]).includes(raw) ? (raw as BrandSection) : DEFAULT_SECTION
}

/**
 * The voice side's nav hint, from the server's review state — never inferred.
 * `usable` is whether a proposed/confirmed block is one the card can render; a row that says
 * `proposed` with a block nobody can read is not something to confirm.
 * `undefined` (still loading, or the read failed) shows no hint rather than a guess.
 */
export const soundHint = (state: VoiceState | undefined, usable: boolean): string | undefined => {
  switch (state) {
    case 'idle': return 'Not read'
    case 'deriving': return 'Reading…'
    case 'proposed': return usable ? 'To confirm' : 'Read it again'
    case 'confirmed': return usable ? 'Confirmed' : 'Read it again'
    default: return undefined
  }
}

/**
 * How we look's status, the server's word for it — never inferred (MAR-391).
 *
 * Detection runs only when How we look OPENS (MAR-334: "a later Shopify change becomes a new
 * suggestion the next time Brand styling opens") — never because the overview rendered. Until it
 * has run this visit, the status is the STORED observation (`GET …/email-brand-detection`, no
 * detection, no write). Once it has run, its verdict wins, whatever it was: an `unavailable`
 * run is no status, not a fall back to the stored one — How we look would show nothing to
 * confirm, so the overview must not say there is something.
 *
 * - `unread`  → "Not read": detection has never observed this shop.
 * - `proposed`→ "To confirm".
 * - `current` → "Up to date". Never "Confirmed": `current` also covers hand-saved styles with no
 *               Shopify evidence to confirm.
 * - anything else (`stale`, `unavailable`, still loading, a failed read) → no status.
 */
export type LookStatus = 'unread' | 'proposed' | 'current'
export const lookStatusOf = (
  live: EmailBrandDetectionReview['state'] | undefined,
  stored: EmailBrandLookRead['state'] | undefined
): LookStatus | undefined => {
  if (live !== undefined) return live === 'proposed' || live === 'current' ? live : undefined
  return stored === 'unread' || stored === 'proposed' || stored === 'current' ? stored : undefined
}

/** The look side's nav hint, from `lookStatusOf`. No status, no hint rather than a guess. */
export const lookHint = (status: LookStatus | undefined): string | undefined => {
  if (status === 'proposed') return 'To confirm'
  if (status === 'current') return 'Up to date'
  if (status === 'unread') return 'Not read'
  return undefined
}
